import React from 'react';
import { Deal } from '../types/pipeline';
import { DollarSign, Calendar, Home, TrendingUp } from 'lucide-react';
import { ViewDensity } from './ViewControls';
interface PipelineCardProps {
  deal: Deal;
  density?: ViewDensity;
  variant?: 'horizontal' | 'stacked';
  onClick?: () => void;
}
export function PipelineCard({
  deal,
  density = 'comfortable',
  variant = 'horizontal',
  onClick
}: PipelineCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatCurrencyCompact = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(amount);
  };
  // Color coding based on days in stage (alert if stagnant)
  const getAgeColor = (days: number) => {
    if (days > 14) return 'text-red-600 bg-red-50 border-red-200';
    if (days > 7) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-slate-500 bg-slate-50 border-slate-200';
  };
  const getProbabilityColor = (probability: number) => {
    if (probability >= 80) return 'bg-emerald-500';
    if (probability >= 50) return 'bg-blue-500';
    if (probability >= 30) return 'bg-amber-500';
    return 'bg-slate-400';
  };
  // STACKED VARIANT - Optimized for vertical display
  if (variant === 'stacked') {
    // Ultra-compact for compact density
    if (density === 'compact') {
      return <div onClick={onClick} className="group relative w-full bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all duration-200 cursor-pointer overflow-hidden" data-id="element-199">
          {/* Probability bar on left edge */}
          <div className="absolute left-0 top-0 w-1 h-full bg-slate-100" data-id="element-200">
            <div className={`w-full ${getProbabilityColor(deal.probability)}`} style={{
            height: `${deal.probability}%`
          }} data-id="element-201" />
          </div>

          <div className="pl-3 pr-2 py-2 flex items-center justify-between" data-id="element-202">
            <div className="flex-1 min-w-0 mr-2" data-id="element-203">
              <h3 className="font-semibold text-xs text-slate-900 truncate" title={deal.borrowerName} data-id="element-204">
                {deal.borrowerName}
              </h3>
              <div className="flex items-center text-xs text-slate-600 font-medium" data-id="element-205">
                {formatCurrencyCompact(deal.amount)}
              </div>
            </div>

            <div className="flex items-center space-x-1.5 flex-shrink-0" data-id="element-206">
              <div className={`text-xs px-1.5 py-0.5 rounded border ${getAgeColor(deal.daysInStage)}`} data-id="element-207">
                {deal.daysInStage}d
              </div>
            </div>
          </div>
        </div>;
    }
    // Comfortable stacked view
    if (density === 'comfortable') {
      return <div onClick={onClick} className="group relative w-full bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden" data-id="element-208">
          {/* Probability bar on left edge */}
          <div className="absolute left-0 top-0 w-1 h-full bg-slate-100" data-id="element-209">
            <div className={`w-full ${getProbabilityColor(deal.probability)}`} style={{
            height: `${deal.probability}%`
          }} data-id="element-210" />
          </div>

          <div className="pl-3 pr-3 py-2.5" data-id="element-211">
            <div className="flex items-start justify-between mb-2" data-id="element-212">
              <h3 className="font-semibold text-sm text-slate-900 truncate pr-2 flex-1" title={deal.borrowerName} data-id="element-213">
                {deal.borrowerName}
              </h3>
              <div className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${getAgeColor(deal.daysInStage)}`} data-id="element-214">
                {deal.daysInStage}d
              </div>
            </div>

            <div className="flex items-center justify-between" data-id="element-215">
              <div className="flex items-center text-slate-700 font-semibold text-sm" data-id="element-216">
                <DollarSign className="w-3.5 h-3.5 mr-0.5 text-slate-400" data-id="element-217" />
                {formatCurrencyCompact(deal.amount)}
              </div>

              <div className="flex items-center text-xs text-slate-500" data-id="element-218">
                <TrendingUp className="w-3 h-3 mr-1" data-id="element-219" />
                <span data-id="element-220">{deal.probability}%</span>
              </div>
            </div>

            <div className="mt-2 pt-2 border-t border-slate-100" data-id="element-221">
              <div className="flex items-center text-xs text-slate-500" data-id="element-222">
                <Home className="w-3 h-3 mr-1.5" data-id="element-223" />
                <span className="truncate" data-id="element-224">{deal.loanType}</span>
              </div>
            </div>
          </div>
        </div>;
    }
    // Spacious stacked view
    return <div onClick={onClick} className="group relative w-full bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden" data-id="element-225">
        {/* Probability bar on left edge */}
        <div className="absolute left-0 top-0 w-1.5 h-full bg-slate-100" data-id="element-226">
          <div className={`w-full ${getProbabilityColor(deal.probability)}`} style={{
          height: `${deal.probability}%`
        }} data-id="element-227" />
        </div>

        <div className="pl-4 pr-3 py-3" data-id="element-228">
          <div className="flex items-start justify-between mb-2" data-id="element-229">
            <h3 className="font-semibold text-sm text-slate-900 pr-2 flex-1" title={deal.borrowerName} data-id="element-230">
              {deal.borrowerName}
            </h3>
            <div className={`text-xs px-2 py-1 rounded-full border flex-shrink-0 font-medium ${getAgeColor(deal.daysInStage)}`} data-id="element-231">
              <Calendar className="w-3 h-3 inline mr-1" data-id="element-232" />
              {deal.daysInStage}d
            </div>
          </div>

          <div className="flex items-center text-slate-700 font-bold text-base mb-3" data-id="element-233">
            <DollarSign className="w-4 h-4 mr-1 text-slate-400" data-id="element-234" />
            {formatCurrency(deal.amount)}
          </div>

          <div className="space-y-2" data-id="element-235">
            <div className="flex items-center text-xs text-slate-500" data-id="element-236">
              <Home className="w-3 h-3 mr-2" data-id="element-237" />
              <span data-id="element-238">
                {deal.loanType} • {deal.propertyType}
              </span>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100" data-id="element-239">
              <span className="text-xs text-slate-500" data-id="element-240">Probability:</span>
              <div className="flex items-center" data-id="element-241">
                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden mr-2" data-id="element-242">
                  <div className={`h-full ${getProbabilityColor(deal.probability)}`} style={{
                  width: `${deal.probability}%`
                }} data-id="element-243" />
                </div>
                <span className="text-xs font-semibold text-slate-700" data-id="element-244">
                  {deal.probability}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // HORIZONTAL VARIANT - Original card designs (kept for backwards compatibility)
  // Compact horizontal view
  if (density === 'compact') {
    return <div onClick={onClick} className="group relative w-[140px] flex-shrink-0 bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer overflow-hidden" data-id="element-245">
        <div className="absolute top-0 left-0 h-1 bg-slate-100 w-full" data-id="element-246">
          <div className="h-full bg-gradient-to-r from-blue-400 to-green-400" style={{
          width: `${deal.probability}%`
        }} data-id="element-247" />
        </div>

        <div className="p-2 pt-3" data-id="element-248">
          <h3 className="font-semibold text-xs text-slate-900 truncate mb-1" title={deal.borrowerName} data-id="element-249">
            {deal.borrowerName}
          </h3>

          <div className="flex items-center text-slate-700 font-medium text-xs mb-2" data-id="element-250">
            <DollarSign className="w-3 h-3 mr-0.5 text-slate-400" data-id="element-251" />
            {formatCurrencyCompact(deal.amount)}
          </div>

          <div className={`flex items-center text-xs px-1.5 py-0.5 rounded-full w-fit ${getAgeColor(deal.daysInStage)}`} data-id="element-252">
            <Calendar className="w-2.5 h-2.5 mr-1" data-id="element-253" />
            <span data-id="element-254">{deal.daysInStage}d</span>
          </div>
        </div>
      </div>;
  }
  // Spacious horizontal view
  if (density === 'spacious') {
    return <div onClick={onClick} className="group relative w-[240px] flex-shrink-0 bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer overflow-hidden" data-id="element-255">
        <div className="absolute top-0 left-0 h-1 bg-slate-100 w-full" data-id="element-256">
          <div className="h-full bg-gradient-to-r from-blue-400 to-green-400" style={{
          width: `${deal.probability}%`
        }} data-id="element-257" />
        </div>

        <div className="p-4 pt-5" data-id="element-258">
          <div className="flex justify-between items-start mb-3" data-id="element-259">
            <h3 className="font-semibold text-slate-900 truncate pr-2" title={deal.borrowerName} data-id="element-260">
              {deal.borrowerName}
            </h3>
          </div>

          <div className="flex items-center text-slate-700 font-bold text-base mb-4" data-id="element-261">
            <DollarSign className="w-4 h-4 mr-1 text-slate-400" data-id="element-262" />
            {formatCurrency(deal.amount)}
          </div>

          <div className="space-y-2" data-id="element-263">
            <div className="flex items-center text-sm text-slate-500" data-id="element-264">
              <Home className="w-3.5 h-3.5 mr-2" data-id="element-265" />
              <span className="truncate" data-id="element-266">{deal.loanType}</span>
            </div>

            <div className="flex items-center text-sm text-slate-500" data-id="element-267">
              <span className="truncate" data-id="element-268">{deal.propertyType}</span>
            </div>

            <div className={`flex items-center text-xs px-2 py-1.5 rounded-full w-fit ${getAgeColor(deal.daysInStage)}`} data-id="element-269">
              <Calendar className="w-3 h-3 mr-1.5" data-id="element-270" />
              <span data-id="element-271">{deal.daysInStage} days in stage</span>
            </div>

            <div className="pt-2 border-t border-slate-100" data-id="element-272">
              <div className="flex items-center justify-between text-xs text-slate-500" data-id="element-273">
                <span data-id="element-274">Probability:</span>
                <span className="font-semibold text-slate-700" data-id="element-275">
                  {deal.probability}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Comfortable horizontal view (default)
  return <div onClick={onClick} className="group relative w-[200px] flex-shrink-0 bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer overflow-hidden" data-id="element-276">
      <div className="absolute top-0 left-0 h-1 bg-slate-100 w-full" data-id="element-277">
        <div className="h-full bg-gradient-to-r from-blue-400 to-green-400" style={{
        width: `${deal.probability}%`
      }} data-id="element-278" />
      </div>

      <div className="p-3 pt-4" data-id="element-279">
        <div className="flex justify-between items-start mb-2" data-id="element-280">
          <h3 className="font-semibold text-slate-900 truncate pr-2" title={deal.borrowerName} data-id="element-281">
            {deal.borrowerName}
          </h3>
        </div>

        <div className="flex items-center text-slate-700 font-medium mb-3" data-id="element-282">
          <DollarSign className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-283" />
          {formatCurrency(deal.amount)}
        </div>

        <div className="space-y-1.5" data-id="element-284">
          <div className="flex items-center text-xs text-slate-500" data-id="element-285">
            <Home className="w-3 h-3 mr-1.5" data-id="element-286" />
            <span className="truncate" data-id="element-287">
              {deal.loanType} • {deal.propertyType}
            </span>
          </div>

          <div className={`flex items-center text-xs px-2 py-1 rounded-full w-fit ${getAgeColor(deal.daysInStage)}`} data-id="element-288">
            <Calendar className="w-3 h-3 mr-1.5" data-id="element-289" />
            <span data-id="element-290">{deal.daysInStage}d in stage</span>
          </div>
        </div>
      </div>
    </div>;
}